import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { modalities } from '@/mocks/modalities';

export default function ModalitiesSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 100}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 leading-tight">
            {t('home.modalities.title')}
          </h2>
          <p className="reveal-item reveal font-body text-base text-foreground-600 max-w-md">
            {t('home.modalities.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {modalities.map((mod) => (
            <div
              key={mod.id}
              className="reveal-item reveal group relative overflow-hidden rounded-lg cursor-pointer transition-transform duration-500 hover:-translate-y-2"
              onClick={() => navigate('/modalidades')}
            >
              <div className="aspect-[3/4] w-full overflow-hidden">
                <img
                  src={mod.image}
                  alt={t(`modality.${mod.id}.name`)}
                  loading="lazy"
                  fetchPriority="low"
                  width="600"
                  height="800"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent group-hover:from-foreground-950/95 group-hover:via-foreground-950/40 transition-all duration-500"></div>

              {mod.tag && (
                <div className="absolute top-3 right-3 bg-primary-500 text-foreground-950 font-body text-xs font-bold px-3 py-1 rounded-full transition-transform duration-500 group-hover:scale-105">
                  {t('home.modalities.tagPopular')}
                </div>
              )}

              <div className="absolute top-3 left-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/0 group-hover:bg-background-50/20 backdrop-blur-sm transition-all duration-500 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0">
                <i className="ri-arrow-right-line text-background-50 text-sm"></i>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="font-heading text-2xl md:text-3xl text-background-50">
                  {t(`modality.${mod.id}.name`)}
                </h3>
                <p className="font-body text-sm text-background-200 mt-1 line-clamp-2 group-hover:line-clamp-none transition-all duration-500">
                  {t(`modality.${mod.id}.description`)}
                </p>
                <p className="font-body text-xs text-primary-500 mt-2 transition-colors duration-500 group-hover:text-primary-400">
                  {t(`modality.${mod.id}.audience`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}